import { useState } from 'react'
import { format, startOfMonth, subMonths, addMonths } from 'date-fns'
import { fr } from 'date-fns/locale'
import { ChevronLeft, ChevronRight, Download, FileSpreadsheet, FileText, Building2 } from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'

const fmt2 = (n: number) => n.toFixed(2)
const fmtH = (n: number) => n > 0 ? n.toFixed(2) : '0.00'

export default function Export() {
  const [monthDate, setMonthDate] = useState(() => startOfMonth(subMonths(new Date(), 1)))
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<any>(null)
  const { current: establishment } = useEstablishment()

  const monthStr = format(monthDate, 'yyyy-MM')

  async function loadData() {
    if (!establishment) return
    setLoading(true)
    try {
      const r = await api.get(`/payroll?month=${monthStr}&establishmentId=${establishment.id}`)
      setData(r.data)
    } finally { setLoading(false) }
  }

  // Export CSV complet (pour cabinet comptable)
  function exportCSVComplet() {
    if (!data) return
    const rows = [
      ['=== RAPPORT MENSUEL IDCC 1505 ==='],
      [`Période : ${format(monthDate, 'MMMM yyyy', { locale: fr })}`],
      [`Établissement : ${establishment?.name}`],
      [`Exporté le : ${format(new Date(), 'dd/MM/yyyy HH:mm')}`],
      [],
      ['Employé', 'Niveau CCN', 'Taux €/h', 'Contrat', 'H. contract.', 'H. totales', 'H. normales',
       'HS +25%', 'HS +50%', 'Nuit 21h-22h', 'Nuit 22h-6h',
       'Dim. matin', 'Dim. après-midi', 'Jours fériés', '1er mai',
       'CP (j)', 'Absence non payée (j)', 'Base salariale €', 'Brut estimé €'],
      ...data.employees.map((e: any) => {
        const basePay = e.totalHours * e.hourlyRate
        return [
          `${e.firstName} ${e.lastName}`,
          e.conventionLevel,
          fmt2(e.hourlyRate),
          e.contractType,
          fmtH(e.weeklyHours),
          fmtH(e.totalHours),
          fmtH(e.normalHours),
          fmtH(e.overtime25Hours),
          fmtH(e.overtime50Hours),
          fmtH(e.night1Hours),
          fmtH(e.night2Hours),
          fmtH(e.sundayMorningHours),
          fmtH(e.sundayAfternoonHours),
          fmtH(e.holidayHours),
          fmtH(e.may1stHours),
          e.paidAbsenceDays,
          e.unpaidAbsenceDays,
          fmt2(basePay),
          fmt2(e.estimatedGross)
        ]
      }),
      [],
      ['TOTAUX', '', '', '', '',
        fmtH(data.employees.reduce((s: number, e: any) => s + e.totalHours, 0)),
        fmtH(data.employees.reduce((s: number, e: any) => s + e.normalHours, 0)),
        fmtH(data.employees.reduce((s: number, e: any) => s + e.overtime25Hours, 0)),
        fmtH(data.employees.reduce((s: number, e: any) => s + e.overtime50Hours, 0)),
        fmtH(data.employees.reduce((s: number, e: any) => s + e.night1Hours, 0)),
        fmtH(data.employees.reduce((s: number, e: any) => s + e.night2Hours, 0)),
        fmtH(data.employees.reduce((s: number, e: any) => s + e.sundayMorningHours, 0)),
        fmtH(data.employees.reduce((s: number, e: any) => s + e.sundayAfternoonHours, 0)),
        fmtH(data.employees.reduce((s: number, e: any) => s + e.holidayHours, 0)),
        fmtH(data.employees.reduce((s: number, e: any) => s + e.may1stHours, 0)),
        data.employees.reduce((s: number, e: any) => s + e.paidAbsenceDays, 0),
        data.employees.reduce((s: number, e: any) => s + e.unpaidAbsenceDays, 0),
        '',
        fmt2(data.employees.reduce((s: number, e: any) => s + e.estimatedGross, 0))
      ]
    ]

    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `paie_${monthStr}_${establishment?.name || 'export'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Export format SILAE (CSV compatible import SILAE)
  function exportSILAE() {
    if (!data) return
    const rows = [
      ['Matricule', 'Nom', 'Prenom', 'CodeRubrique', 'Libelle', 'NbHeures', 'TauxHoraire', 'Montant'],
      ...data.employees.flatMap((e: any, i: number) => {
        const mat = String(i + 1).padStart(4, '0')
        const lines = []
        if (e.normalHours > 0)
          lines.push([mat, e.lastName, e.firstName, '1000', 'Heures normales', fmtH(e.normalHours), fmt2(e.hourlyRate), fmt2(e.normalHours * e.hourlyRate)])
        if (e.overtime25Hours > 0)
          lines.push([mat, e.lastName, e.firstName, '1025', 'HS +25%', fmtH(e.overtime25Hours), fmt2(e.hourlyRate * 1.25), fmt2(e.overtime25Hours * e.hourlyRate * 1.25)])
        if (e.overtime50Hours > 0)
          lines.push([mat, e.lastName, e.firstName, '1050', 'HS +50%', fmtH(e.overtime50Hours), fmt2(e.hourlyRate * 1.5), fmt2(e.overtime50Hours * e.hourlyRate * 1.5)])
        if (e.sundayAfternoonHours > 0)
          lines.push([mat, e.lastName, e.firstName, '2010', 'Maj. dimanche', fmtH(e.sundayAfternoonHours), fmt2(e.hourlyRate * 0.2), fmt2(e.sundayAfternoonHours * e.hourlyRate * 0.2)])
        if (e.night2Hours > 0)
          lines.push([mat, e.lastName, e.firstName, '2020', 'Maj. nuit', fmtH(e.night2Hours), fmt2(e.hourlyRate * 0.25), fmt2(e.night2Hours * e.hourlyRate * 0.25)])
        return lines
      })
    ]
    const csv = rows.map(r => r.join(';')).join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `SILAE_${monthStr}_${establishment?.name || 'export'}.csv`
    a.click()
  }

  // Export format Sage Paie (CSV compatible)
  function exportSage() {
    if (!data) return
    const rows = [
      ['CODE_SAL', 'NOM', 'PRENOM', 'RUBRIQUE', 'BASE', 'TAUX', 'MONTANT', 'PERIODE'],
      ...data.employees.flatMap((e: any, i: number) => {
        const code = String(i + 1).padStart(3, '0')
        return [
          [code, e.lastName, e.firstName, 'SAL001', fmtH(e.normalHours), fmt2(e.hourlyRate), fmt2(e.normalHours * e.hourlyRate), monthStr],
          ...(e.overtime25Hours > 0 ? [[code, e.lastName, e.firstName, 'HS25', fmtH(e.overtime25Hours), fmt2(e.hourlyRate * 1.25), fmt2(e.overtime25Hours * e.hourlyRate * 1.25), monthStr]] : []),
          ...(e.overtime50Hours > 0 ? [[code, e.lastName, e.firstName, 'HS50', fmtH(e.overtime50Hours), fmt2(e.hourlyRate * 1.5), fmt2(e.overtime50Hours * e.hourlyRate * 1.5), monthStr]] : []),
        ]
      })
    ]
    const csv = rows.map(r => r.join(';')).join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `Sage_${monthStr}_${establishment?.name || 'export'}.csv`
    a.click()
  }

  // Export DSN-like simplifié (structure pour transmission DRH ou expert-comptable)
  function exportDSN() {
    if (!data) return
    const lines: string[] = [
      `S10.G00.00.001:'${establishment?.name}'`,
      `S10.G00.00.002:'SIRET_A_RENSEIGNER'`,
      `S10.G00.00.003:'${monthStr}'`,
      `S10.G00.00.004:'IDCC1505'`,
      '',
      ...data.employees.flatMap((e: any, i: number) => [
        `S20.G00.30.001-${i + 1}:'${e.lastName}'`,
        `S20.G00.30.002-${i + 1}:'${e.firstName}'`,
        `S20.G00.30.006-${i + 1}:'${e.contractType}'`,
        `S20.G00.30.007-${i + 1}:${e.weeklyHours}`,
        `S20.G00.30.010-${i + 1}:${e.conventionLevel}`,
        `S21.G00.51.001-${i + 1}:${fmtH(e.totalHours)}`,
        `S21.G00.51.002-${i + 1}:${fmtH(e.normalHours)}`,
        `S21.G00.51.011-${i + 1}:${fmtH(e.overtime25Hours)}`,
        `S21.G00.51.012-${i + 1}:${fmtH(e.overtime50Hours)}`,
        `S21.G00.51.020-${i + 1}:${fmtH(e.night1Hours)}`,
        `S21.G00.51.021-${i + 1}:${fmtH(e.night2Hours)}`,
        `S21.G00.51.030-${i + 1}:${fmtH(e.sundayAfternoonHours)}`,
        `S21.G00.51.040-${i + 1}:${fmtH(e.holidayHours)}`,
        `S21.G00.51.041-${i + 1}:${fmtH(e.may1stHours)}`,
        `S21.G00.40.001-${i + 1}:${fmt2(e.estimatedGross)}`,
        ''
      ])
    ]

    const content = lines.join('\n')
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `dsn_${monthStr}_${establishment?.name || 'export'}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  const totalGross = data?.employees.reduce((s: number, e: any) => s + e.estimatedGross, 0) || 0
  const totalHours = data?.employees.reduce((s: number, e: any) => s + e.totalHours, 0) || 0

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <FileSpreadsheet size={22} className="text-blue-600" />
        <h1 className="text-xl font-bold text-gray-900">Export Comptabilité</h1>
      </div>

      {/* Sélecteur mois */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-1 bg-white border rounded-lg shadow-sm">
          <button onClick={() => setMonthDate(d => subMonths(d, 1))} className="p-2 hover:bg-gray-50 rounded-l-lg">
            <ChevronLeft size={18} />
          </button>
          <span className="px-4 text-sm font-medium text-gray-700 capitalize">
            {format(monthDate, 'MMMM yyyy', { locale: fr })}
          </span>
          <button onClick={() => setMonthDate(d => addMonths(d, 1))} className="p-2 hover:bg-gray-50 rounded-r-lg">
            <ChevronRight size={18} />
          </button>
        </div>
        <button
          onClick={loadData}
          disabled={loading || !establishment}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
          {loading ? 'Chargement...' : 'Charger les données'}
        </button>
      </div>

      {!data ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
          <FileSpreadsheet size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400 text-sm">Sélectionnez un mois et chargez les données</p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Résumé */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Masse salariale brute</p>
              <p className="text-2xl font-bold text-gray-900">{totalGross.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
              <p className="text-xs text-gray-400 mt-1">Estimation IDCC 1505</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total heures travaillées</p>
              <p className="text-2xl font-bold text-gray-900">{totalHours.toFixed(1)} h</p>
              <p className="text-xs text-gray-400 mt-1">{data.employees.length} employés actifs</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Coût moyen / heure</p>
              <p className="text-2xl font-bold text-gray-900">
                {totalHours > 0 ? (totalGross / totalHours).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) : '—'} €
              </p>
              <p className="text-xs text-gray-400 mt-1">Taux horaire moyen</p>
            </div>
          </div>

          {/* Tableau récapitulatif */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 text-sm">Récapitulatif par employé</h2>
              <div className="flex gap-2">
                <button
                  onClick={exportCSVComplet}
                  className="flex items-center gap-2 text-sm text-gray-700 border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg transition-colors">
                  <Download size={14} /> CSV (comptable)
                </button>
                <button
                  onClick={exportDSN}
                  className="flex items-center gap-2 text-sm text-blue-700 border border-blue-200 hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors">
                  <FileText size={14} /> DSN simplifié
                </button>
                <button
                  onClick={exportSILAE}
                  className="flex items-center gap-2 text-sm text-purple-700 border border-purple-200 hover:bg-purple-50 px-3 py-1.5 rounded-lg transition-colors">
                  <Download size={14} /> SILAE
                </button>
                <button
                  onClick={exportSage}
                  className="flex items-center gap-2 text-sm text-green-700 border border-green-200 hover:bg-green-50 px-3 py-1.5 rounded-lg transition-colors">
                  <Download size={14} /> Sage Paie
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Employé</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Niveau</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Taux €/h</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">H. totales</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">HS +25%</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">HS +50%</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Dim. ap-midi</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Fériés</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Brut estimé</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.employees.map((e: any) => (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {e.firstName[0]}{e.lastName[0]}
                          </div>
                          <span className="font-medium text-gray-900 text-sm">{e.firstName} {e.lastName}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-medium">{e.conventionLevel}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center text-xs text-gray-600">{fmt2(e.hourlyRate)} €</td>
                      <td className="px-3 py-2.5 text-center font-semibold text-gray-900">{fmtH(e.totalHours)}h</td>
                      <td className="px-3 py-2.5 text-center text-xs text-orange-600">{e.overtime25Hours > 0 ? `${fmtH(e.overtime25Hours)}h` : '—'}</td>
                      <td className="px-3 py-2.5 text-center text-xs text-red-600">{e.overtime50Hours > 0 ? `${fmtH(e.overtime50Hours)}h` : '—'}</td>
                      <td className="px-3 py-2.5 text-center text-xs text-amber-600">{e.sundayAfternoonHours > 0 ? `${fmtH(e.sundayAfternoonHours)}h` : '—'}</td>
                      <td className="px-3 py-2.5 text-center text-xs text-purple-600">{e.holidayHours > 0 ? `${fmtH(e.holidayHours)}h` : '—'}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-gray-900">{e.estimatedGross.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-gray-50">
                    <td colSpan={3} className="px-4 py-2.5 text-xs font-semibold text-gray-600">TOTAUX</td>
                    <td className="px-3 py-2.5 text-center text-xs font-bold text-gray-900">{fmtH(totalHours)}h</td>
                    <td className="px-3 py-2.5 text-center text-xs font-bold text-orange-600">
                      {fmtH(data.employees.reduce((s: number, e: any) => s + e.overtime25Hours, 0))}h
                    </td>
                    <td className="px-3 py-2.5 text-center text-xs font-bold text-red-600">
                      {fmtH(data.employees.reduce((s: number, e: any) => s + e.overtime50Hours, 0))}h
                    </td>
                    <td className="px-3 py-2.5 text-center text-xs font-bold text-amber-600">
                      {fmtH(data.employees.reduce((s: number, e: any) => s + e.sundayAfternoonHours, 0))}h
                    </td>
                    <td className="px-3 py-2.5 text-center text-xs font-bold text-purple-600">
                      {fmtH(data.employees.reduce((s: number, e: any) => s + e.holidayHours, 0))}h
                    </td>
                    <td className="px-3 py-2.5 text-right font-bold text-gray-900 text-sm">
                      {totalGross.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Note DSN */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
            <Building2 size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Export DSN simplifié</p>
              <p className="text-blue-700 text-xs">
                Le fichier DSN généré est une aide à la préparation de votre Déclaration Sociale Nominative.
                Il doit être vérifié et complété par votre expert-comptable avant transmission à l'URSSAF.
                Le SIRET doit être renseigné dans le fichier généré.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
